import mapboxgl from "mapbox-gl";
import { allSelectableLayers } from "../../constants/constants";
import { getFeaturesCardsGroupIds,  
        getRelatedFeatureIds, 
        removeRepeatedFeatures,
        getHoveredFeatures,
        groupCardsGroupIds,
        getDataFromFeature } from "../cardsGroupsFeatures";
import { store } from "../..";
import { postData } from "../postData.js";
import { reloadCardsGroupsFeatures } from "./reloadCardsGroups";
import { showMessage } from "../popUpMessage.js";
import { _ } from "../translator";

export function highlightManager(map, props, handleOpenPopup) {
    let start;
    let current;
    let box;
    const canvas = map.getCanvasContainer();
    canvas.addEventListener('mousedown', mouseDown, true);

    document.addEventListener('keydown', keyDown);
    window.addEventListener('blur', resetCtrlState);
    document.addEventListener('visibilitychange', onVisibilityChange);

    let ctrlFeatures = []
    let keyPressed = false

    function resetCtrlState() {
        if (!keyPressed) return;
        map.off('click', getCtrlFeatures);
        map.on('click', handleOpenPopup);
        removeHighlight(map, ctrlFeatures.length > 0 ? ctrlFeatures : null);
        ctrlFeatures = [];
        keyPressed = false;
        document.removeEventListener('keyup', keyUp);
    }

    function onVisibilityChange() {
        if (document.hidden) {
            resetCtrlState();
        }
    }

    function keyDown(e) {
        const state = store.getState()
        
        if (e.ctrlKey && e.key === 'a') {
            e.preventDefault();
            handleCtrlAMerge();
            return;
        }
        
        if (state.popUpMenu.show) {
            if (!e.metaKey) return
        } else {
            if (!e.ctrlKey && !e.metaKey) return
        }
        if (!keyPressed) {
            document.addEventListener('keyup', keyUp);
            keyPressed = true
            map.off('click', handleOpenPopup)
            map.on('click', getCtrlFeatures)
        }
    }

    async function handleCtrlAMerge() {
        const state = store.getState();
        const selectedCardsGroups = state.selectedCardsGroups;
        
        if (selectedCardsGroups === 'none' || selectedCardsGroups.length === 0) {
            return;
        }

        const propsFromStore = {
            projectData: state.projectData,
            cardsGroupsWithHistoricalObservations: state.cardsGroupsWithHistoricalObservations || []
        };

        const grouped = groupCardsGroupIds(propsFromStore, selectedCardsGroups);
        const mergeableCardsGroups = grouped.mergeable;

        if (mergeableCardsGroups.cardsGroupIds.length < 2) {
            return;
        }

        props.popUpMenuChange({
            show: false,
            close: false,
            opacity: props.popUpMenu.opacity ? 0 : 1,
            cyclesButton: false
        });
        props.selectedCardsGroupsChange([]);
        
        const message = _("Agrupando Protocolos") + '...';
        showMessage(props, message, { loading: true });
        
        let urlPost = {
            project_id: window.project_id,
            cards_group_ids: mergeableCardsGroups.cardsGroupIds,
            figure_ids: mergeableCardsGroups.figureIds
        };
        
        let response = await postData('/web-api/merge-cards-groups', urlPost);
        handleMergeResponse(response, mergeableCardsGroups);
    }

    function handleMergeResponse(response, mergeableCardsGroups) {
        if(response) {
            props.deleteCards(mergeableCardsGroups.cardsGroupIds);
            const state = store.getState();
            const propsForReload = {
                projectData: state.projectData,
                editCards: props.editCards,
                editFeaturesGroup: props.editFeaturesGroup,
                editFigures: props.editFigures
            };
            reloadCardsGroupsFeatures(propsForReload, response.new_cards_group_ids);
            showMessage(props, '¡'+ _('Se han agrupado los protocolos correctamente')+ '!');
        } else {
            showMessage(props, _('No se pudieron agrupar los protocolos'));
        }
    }

    function keyUp() {
        const featuresToProcess = [...ctrlFeatures];
        resetCtrlState();
        if (featuresToProcess.length === 0) return;

        const state = store.getState();
        const whatsappObservationsMode = state.switches.whatsappObservationsMode;
        
        if (whatsappObservationsMode) {
            const { figuregroupIds, levelIds } = getFiguregroupIdsFromFeatures(featuresToProcess);
            if (figuregroupIds.length > 0) {
                const observationIds = getWhatsAppObservationIdsFromFiguregroups(
                    figuregroupIds, 
                    levelIds, 
                    state.whatsappObservations.observations,
                    state.visibleWhatsAppObservationsIds
                );
                if (observationIds.length > 0) {
                    props.setWhatsAppObservationsPopup({ show: true });
                    props.setSelectedWhatsAppObservations(observationIds);
                } else {
                    props.setWhatsAppObservationsPopup({ show: false, expanded: false });
                }
            } else {
                props.setWhatsAppObservationsPopup({ show: false, expanded: false });
            }
        } else {
            let cardsGroupIds = getFeaturesCardsGroupIds(map, featuresToProcess);
            if (cardsGroupIds.length > 0) {
                props.popUpMenuChange({ show: true });
                props.selectedCardsGroupsChange(cardsGroupIds);
            } else {
                props.popUpMenuChange({ show: false, expanded: false, cyclesButton: false, changeMenuButton: false });
            }
        }
    }
    
    function getFiguregroupIdsFromFeatures(features) {
        const figuregroupIds = [];
        let levelIds = [];
        for (const feature of features) {
            const { figuregroupIds: featureFiguregroupIds, levelId } = getDataFromFeature(feature);
            figuregroupIds.push(...featureFiguregroupIds);
            levelIds.push(levelId);
        }
        return { figuregroupIds, levelIds };
    }
    
    function getWhatsAppObservationIdsFromFiguregroups(figuregroupIds, levelIds, whatsappObservations, visibleWhatsAppObservationsIds = []) {
        const observationIds = [];
        if (!whatsappObservations) return observationIds;
        
        // Convert to Sets for O(1) lookup instead of O(n) with includes()
        const figuregroupIdsSet = new Set(figuregroupIds.map(id => String(id)));
        const levelIdsSet = new Set(levelIds.map(id => parseInt(id)));
        const visibleObservationsSet = new Set(visibleWhatsAppObservationsIds);
        
        for (const observationId in whatsappObservations) {
            // First check if this observation is in the visible list (filtered)
            if (visibleObservationsSet.size > 0 && !visibleObservationsSet.has(observationId)) {
                continue;
            }
            
            const observation = whatsappObservations[observationId];
            if (observation.locations && observation.locations.length > 0) {
                // Use Set.has() for O(1) lookup instead of Array.includes() O(n)
                const hasMatchingLocation = observation.locations.some(location => {
                    if (!location.figuregroup_id) return false;
                    const locFiguregroupId = String(location.figuregroup_id);
                    const locLevelId = parseInt(location.level_id);
                    return figuregroupIdsSet.has(locFiguregroupId) && levelIdsSet.has(locLevelId);
                });
                if (hasMatchingLocation) {
                    observationIds.push(observationId);
                }
            }
        }
        return observationIds;
    }

    function getCtrlFeatures(event) {
        const domEvent = event.originalEvent;
        if (domEvent && !domEvent.ctrlKey && !domEvent.metaKey) {
            resetCtrlState();
            handleOpenPopup(event);
            return;
        }
        let bbox = [
            [event.point.x - 5, event.point.y - 5],
            [event.point.x + 5, event.point.y + 5]
        ];
        let map = event.target;
        const selectedFeatures = map.queryRenderedFeatures(bbox, {
            layers: allSelectableLayers
        });
        const hoveredFeatures = getHoveredFeatures(selectedFeatures);
        if (hoveredFeatures.length) {
            ctrlFeatures = ctrlFeatures.concat(hoveredFeatures);
            addHighlight(map, hoveredFeatures);
        }
    }
    
    function mousePos(e) {
        const rect = canvas.getBoundingClientRect();
        return new mapboxgl.Point(
            e.clientX - rect.left - canvas.clientLeft,
            e.clientY - rect.top - canvas.clientTop
        );
    }

    function mouseDown(e) {
        if (!e.shiftKey || e.ctrlKey || e.metaKey) return;
        map.dragPan.disable();
        map.off('click', handleOpenPopup)
        removeHighlight(map)
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
        start = mousePos(e);
    }
    
    function onMouseMove(e) {
        current = mousePos(e);
        
        if (!box) {
            box = document.createElement('div');
            box.classList.add('boxdraw');
            canvas.appendChild(box);
        }
    
        const minX = Math.min(start.x, current.x),
        maxX = Math.max(start.x, current.x),
        minY = Math.min(start.y, current.y),
        maxY = Math.max(start.y, current.y);
        
        const pos = `translate(${minX}px, ${minY}px)`;
        box.style.transform = pos;
        box.style.width = maxX - minX + 'px';
        box.style.height = maxY - minY + 'px';
    }
    
    function onMouseUp(e) {
        let end = mousePos(e)
        if (start.y < end.y) {
            finish([start, end])
        } else {
            finish([end, start])
        }
    }

    function finish(bbox) {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);

        if (box) {
            box.parentNode.removeChild(box);
            box = null;
        }
         
        if (bbox) {
            const features = map.queryRenderedFeatures(bbox, {
                layers: allSelectableLayers
            });
            let filteredFeatures = removeRepeatedFeatures(features);
            addHighlight(map, filteredFeatures);
            
            const state = store.getState();
            const whatsappObservationsMode = state.switches.whatsappObservationsMode;
            
            if (whatsappObservationsMode) {
                // WhatsApp mode: get observation IDs from figuregroups
                const { figuregroupIds, levelIds } = getFiguregroupIdsFromFeatures(filteredFeatures);
                if (figuregroupIds.length > 0) {
                    const observationIds = getWhatsAppObservationIdsFromFiguregroups(
                        figuregroupIds, 
                        levelIds, 
                        state.whatsappObservations.observations,
                        state.visibleWhatsAppObservationsIds
                    );
                    if (observationIds.length > 0) {
                        props.setWhatsAppObservationsPopup({ show: true });
                        props.setSelectedWhatsAppObservations(observationIds);
                    } else {
                        props.setWhatsAppObservationsPopup({ show: false, expanded: false });
                    }
                } else {
                    props.setWhatsAppObservationsPopup({ show: false, expanded: false });
                }
            } else {
                // Normal mode: get cardsgroup IDs
                let cardsGroupIds = getFeaturesCardsGroupIds(map, filteredFeatures);
                if (cardsGroupIds.length > 0) {
                    props.popUpMenuChange({ show: true });
                    props.selectedCardsGroupsChange(cardsGroupIds);
                } else {
                    props.popUpMenuChange({ show: false, expanded: false, cyclesButton: false, changeMenuButton: false });
                }
            }
        }
        map.on('click', handleOpenPopup);

        map.dragPan.enable();
    }
}

export function addHighlight(map, features) {
    features.forEach(feature => {
        map.setFeatureState(
            { source: 'cards', id: feature.id },
            { highlight: true }
        )
        let relatedFeatureIds = getRelatedFeatureIds(map, feature);
        for (const featureId of relatedFeatureIds) {
            map.setFeatureState(
                { source: 'cards', id: featureId },
                { highlight: true }
            );
        }
    })
}

export function removeHighlight(map, features=null) {
    if (!features) {
        features = map.querySourceFeatures('cards')
    }
    features.forEach(feature => {
        map.setFeatureState(
            { source: 'cards', id: feature.id },
            { highlight: false }
        )
    })
}